import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";

/**
 * The properties for {@link CreateTagDirectoryDialog}
 */
export type CreateTagDirectoryDialogProps = {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when the popup should be closed */
    onClose: () => void;
};

/**
 * A dialog to create a new tag
 */
export default function CreateTagDirectoryDialog(props: CreateTagDirectoryDialogProps) {
    const [t] = useTranslation("dialog-tag-directory");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: { name: "" },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiInternal.tags.createDirectory({
                    name: value.name,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            name: res.error ? t("error.name-not-unique") : undefined,
                            description: undefined,
                        },
                    };
                }
            },
        },
        onSubmit: () => {
            props.onClose();
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen]);

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("heading.create-tag-directory")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"name"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.name")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            required={true}
                                            maxLength={255}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.create-tag-directory")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
